import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { useClientStoredResourcesContext } from '@pflegenavi/frontend/client-stored-resources-context';

interface StoredSelectionOptions {
  /** Client storage key holding the selected id, e.g. `selectedNursingHomeId`. */
  idKey: string;
  /** Client storage key holding the selected item as JSON, e.g. `selectedNursingHome`. */
  objectKey: string;
  /**
   * Id pinned by the URL, e.g. the welcome link's `nh_id`. When it matches an
   * entry of the list it wins over the stored selection.
   */
  urlId?: string | null;
  /** Called once the URL-pinned id has been applied, so the scope can follow it. */
  onUrlIdApplied?: () => void;
}

export interface StoredSelection<T> {
  selected?: T;
  list: T[];
  /** Whether a list has arrived yet, so an empty one can be told from one still loading. */
  isListLoaded: boolean;
  setList: (list: T[]) => Promise<void>;
  select: (item: T) => Promise<void>;
}

/**
 * Keeps one scope selection (a nursing home, a legal entity, ...) in sync with
 * client storage, so a reload restores it before the list has been fetched.
 *
 * @example
 * const nursingHomes = useStoredSelection<NursingHome>({
 *   idKey: 'selectedNursingHomeId',
 *   objectKey: 'selectedNursingHome',
 *   urlId: getQueryParam?.('nh_id'),
 *   onUrlIdApplied: () => setScope({ kind: 'nursing_home' }),
 * });
 */
export const useStoredSelection = <T extends { id: string }>({
  idKey,
  objectKey,
  urlId,
  onUrlIdApplied,
}: StoredSelectionOptions): StoredSelection<T> => {
  const resources = useClientStoredResourcesContext();

  const [selected, setSelected] = useState<T | undefined>(undefined);
  const [list, setListInternal] = useState<T[]>([]);
  const [isListLoaded, setIsListLoaded] = useState(false);
  // The pre-load below and `setList` race each other, and the list has the
  // final say - see the comment on the guard in the pre-load.
  const listApplied = useRef(false);

  useEffect(() => {
    resources.registerResource(idKey);
    resources.registerResource(objectKey);
  }, [resources, idKey, objectKey]);

  useEffect(() => {
    // Pre-load the selection in case we have it, so the app does not wait for
    // the list to arrive before it knows what is selected.
    (async function () {
      const json = await resources.get(objectKey);
      if (!json) {
        return;
      }
      setSelected((current) => {
        // We already received data from the backend - do not overwrite with storage data.
        // The same goes once a list has been applied but left nothing selected:
        // that selection was deliberately dropped because it is not in the list
        // (any more), so restoring it here would bring it straight back.
        if (current || listApplied.current) {
          return current;
        }
        return JSON.parse(json);
      });
    })();
  }, [resources, objectKey]);

  const select = useCallback(
    async (item: T) => {
      // State first, so a caller can batch the selection with a navigation
      // into one render; storage only has to be written by the time it resolves.
      setSelected(item);
      await resources.set(idKey, item.id);
      await resources.set(objectKey, JSON.stringify(item));
    },
    [resources, idKey, objectKey]
  );

  const clear = useCallback(() => {
    resources.remove(idKey);
    resources.remove(objectKey);
    setSelected(undefined);
  }, [resources, idKey, objectKey]);

  const setList = useCallback(
    async (next: T[]) => {
      listApplied.current = true;
      setListInternal(next);
      setIsListLoaded(true);

      if (urlId) {
        const fromUrl = next.find((item) => item.id === urlId);
        if (fromUrl) {
          await select(fromUrl);
          onUrlIdApplied?.();
          return;
        }
      }

      const storedId = await resources.get(idKey);
      if (storedId) {
        const stored = next.find((item) => item.id === storedId);
        if (stored) {
          await select(stored);
          return;
        }
      }

      if (next.length > 0) {
        await select(next[0]);
        return;
      }

      clear();
    },
    [resources, idKey, urlId, onUrlIdApplied, select, clear]
  );

  return useMemo(
    () => ({ selected, list, isListLoaded, setList, select }),
    [selected, list, isListLoaded, setList, select]
  );
};
